import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { adminConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";
import Alert from "components/Alert";
import SimpleModal from "components/SimpleModal";
import IconSpinner from "components/IconSpinner";
import { getLastWorkflowStepForWorkflow } from "actions/adminActions";
import JSONPretty from "react-json-pretty";
import type { RootState } from "types/state";

interface OwnProps {
  workflowId?: string;
  onClose?: () => void;
}

const errorSelector = createErrorSelector(adminConstants.GET_WORKFLOW_STEPS);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getLastWorkflowStepForWorkflow,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

// workflowStep is initialized as {} and later replaced by the resolved
// getLastWorkflowStep payload; it is only fed to JSONPretty (data?: any).
type WorkflowStep = Record<string, unknown>;

interface State {
  isLoading: boolean;
  workflowStep: WorkflowStep;
}

class WorkflowStepModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      workflowStep: {},
    };
  }

  async componentDidMount() {
    // The dispatched thunk resolves to the SUCCESS/FAILURE action union;
    // runtime assumes the success path and reads `.data`. Cast at this
    // boundary to preserve that behavior.
    const { data: workflowStep } =
      (await this.props.getLastWorkflowStepForWorkflow(
        this.props.client as ApolloClient<object>,
        this.props.workflowId as string
      )) as unknown as { data: WorkflowStep };
    this.setState({
      isLoading: false,
      workflowStep,
    });
  }

  _buildMetadataTable = () => {
    if (this.state.isLoading) {
      return <IconSpinner centered />;
    }

    return (
      <JSONPretty
        data={this.state.workflowStep}
        themeClassName="custom-json-pretty"
        style={{ fontSize: "14px", margin: 15 }}
        mainStyle="background:#F7F7F7; line-height:1.3; padding: 20px; ;overflow:auto; color:#999;"
        keyStyle="color:#905;"
        stringStyle="color:#690;"
        valueStyle="color:#905;"
        booleanStyle="color:#905;"
      />
    );
  };

  render() {
    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }

    return (
      <div>
        <SimpleModal
          show={true}
          size={"xl"}
          onClose={() => this.props.onClose!()}
          title="Workflow Step Metadata"
          buttonText="close"
        >
          {this._buildMetadataTable()}
        </SimpleModal>
      </div>
    );
  }
}

export default connector(withApollo<Props>(WorkflowStepModal));
